/**
 * One session at a time.
 *
 * The app kept two independent pairs of keys — token/user for a customer and
 * agent_token/agent_user for an agent — and nothing said the two could not
 * be open at once. They routinely were: the login call wrote the customer
 * pair for an agent as well, so signing in as an agent quietly created a
 * second session, and the header ended up with two buttons.
 *
 * Signing in now closes whatever was open before, the way every other
 * platform does it, and signing out closes only what a session is: the
 * language and any draft the person was in the middle of are theirs, not the
 * session's.
 */

import { forgetAll } from "./freshData";

const KEYS = {
  customer: ["token", "user"],
  agent: ["agent_token", "agent_user"],
};

/** Things a session leaves behind that mean nothing to the next one. */
const SCOPED = [
  "userLookingFor",
  "propertyType",
  "agent_signup_data",
  "agent_invite_token",
  "agent_invite_by",
  "agentEmail",
  "email_verified",
];

const drop = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (e) {
    // site data blocked; there was nothing stored to remove
  }
};

const has = (key) => {
  try {
    const value = localStorage.getItem(key);
    return Boolean(value) && value !== "null" && value !== "undefined";
  } catch (e) {
    return false;
  }
};

/**
 * Is this role signed in?
 *
 * Three screens wrote this test out longhand — a truthy token, of non-zero
 * length, that is not the string "null" and not the string "undefined" —
 * because a token written to storage as `undefined` reads back as those four
 * letters and passes a plain truthy check. `has` below has always known that.
 * The copies did not always agree: one of them checked all four conditions
 * and another only two, in the same file.
 */
export const isSignedIn = (role) =>
  has(role === "agent" ? "agent_token" : "token");

/**
 * Which session an account opens.
 *
 * The answer is in the account itself and nowhere else, which the sign-in by
 * password had right and the sign-in through Google had wrong: it wrote the
 * customer keys for everybody, so an agent who came in that way was an agent
 * to the server and a customer to every screen.
 */
export const roleFor = (userDetail) =>
  userDetail?.user_type === 2 ? "agent" : "customer";

/** The token itself, for the code that has to send it. */
export const tokenFor = (role) => {
  try {
    return localStorage.getItem(role === "agent" ? "agent_token" : "token") || "";
  } catch (e) {
    return "";
  }
};

/** @returns {"customer"|"agent"|null} who is signed in right now. */
export const currentRole = () => {
  if (has("agent_token")) return "agent";
  if (has("token")) return "customer";
  return null;
};

/** The account that is signed in, as the sign-in left it; `null` when none. */
export const currentUser = () => {
  const role = currentRole();
  if (!role) return null;
  try {
    const raw = localStorage.getItem(KEYS[role][1]);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

/**
 * Change something about the signed-in account without signing in again.
 *
 * A phone number added, a name corrected: the session is the same person,
 * only what it knows about them changed. Returns the account as it now is.
 */
export const updateSessionUser = (patch) => {
  const role = currentRole();
  const user = currentUser();
  if (!role || !user) return null;
  const next = { ...user, ...patch };
  try {
    localStorage.setItem(KEYS[role][1], JSON.stringify(next));
  } catch (e) {
    // site data blocked; the change lives in redux for this page only
  }
  return next;
};

/** True when signing in as `role` would close somebody else's session. */
export const wouldReplaceSession = (role) => {
  const open = currentRole();
  return Boolean(open) && open !== role;
};

/** Close one role's session, or every role's when none is named. */
export const closeSession = (role) => {
  const roles = role ? [role] : Object.keys(KEYS);
  roles.forEach((name) => (KEYS[name] || []).forEach(drop));
  SCOPED.forEach(drop);
  /* Whoever signs in next is a different person, and the notes saying "we
     already asked for this" were about the last one. */
  forgetAll();
};

/**
 * Open one, closing the other. Both are written here so the two call sites
 * that used to write the customer keys for an agent cannot drift again.
 */
export const openSession = (role, token, user) => {
  forgetAll();
  Object.keys(KEYS).forEach((name) => {
    if (name !== role) (KEYS[name] || []).forEach(drop);
  });

  const [tokenKey, userKey] = KEYS[role] || [];
  if (!tokenKey) return;

  try {
    if (token) localStorage.setItem(tokenKey, token);
    if (user) localStorage.setItem(userKey, JSON.stringify(user || {}));
  } catch (e) {
    // site data blocked: the session lives for this page only
  }
};
